import React, { useRef, useEffect, useCallback } from 'react';
import { Stage, Layer, Rect, Ellipse, Line as KonvaLine, Shape, Text, Image as KonvaImage, Transformer } from 'react-konva';
import useImage from 'use-image';
import Konva from 'konva';
import { CanvasElement, TextElement, ImageElement, RectElement, CircleElement, LineElement, TriangleElement, CanvasGuides } from '../types';

// react-konva Stage type definitions vary by version; cast to any to avoid prop conflicts
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const KonvaStage = Stage as any;

// Individual element renderers
const KonvaImageEl: React.FC<{
  el: ImageElement;
  isSelected: boolean;
  onSelect: () => void;
  onChange: (patch: Partial<ImageElement>) => void;
}> = ({ el, isSelected, onSelect, onChange }) => {
  const [img] = useImage(el.src, 'anonymous');
  const shapeRef = useRef<Konva.Image>(null);
  const trRef = useRef<Konva.Transformer>(null);

  // Closure-based temperature filter — directly shifts R/B channels per pixel.
  // Avoids Konva.Filters.RGBA which blends toward a target color using the node's
  // opacity as the blend alpha, making the image go black at full opacity.
  const temperature = el.temperature ?? 0;
  const temperatureFilter = useCallback((imageData: ImageData) => {
    const data = imageData.data;
    const shift = temperature * 1.5;
    for (let i = 0; i < data.length; i += 4) {
      data[i]     = Math.max(0, Math.min(255, data[i]     + shift));
      data[i + 2] = Math.max(0, Math.min(255, data[i + 2] - shift));
    }
  }, [temperature]);

  // Build active filter list
  const filters: ((imageData: ImageData) => void)[] = [];
  if (el.brightness) filters.push(Konva.Filters.Brighten);
  if (el.contrast)   filters.push(Konva.Filters.Contrast);
  if (el.saturation) filters.push(Konva.Filters.HSL);
  if (temperature)   filters.push(temperatureFilter);
  if (el.blur)       filters.push(Konva.Filters.Blur);
  if (el.grayscale)  filters.push(Konva.Filters.Grayscale);
  if (el.sepia)      filters.push(Konva.Filters.Sepia);
  const hasFilters = filters.length > 0;

  // Cache node whenever filters or source change (required by Konva)
  useEffect(() => {
    const node = shapeRef.current;
    if (!node || !img) return;
    if (hasFilters) {
      node.cache();
    } else {
      node.clearCache();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [el.brightness, el.contrast, el.saturation, el.temperature, el.blur, el.grayscale, el.sepia, img]);

  useEffect(() => {
    if (isSelected && trRef.current && shapeRef.current) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected]);

  const scaleX = el.flipX ? -1 : 1;
  const scaleY = el.flipY ? -1 : 1;

  return (
    <>
      <KonvaImage
        ref={shapeRef}
        image={img}
        x={el.x}
        y={el.y}
        width={el.width}
        height={el.height}
        rotation={el.rotation}
        opacity={el.opacity}
        visible={el.visible}
        scaleX={scaleX}
        scaleY={scaleY}
        offsetX={el.flipX ? el.width : 0}
        offsetY={el.flipY ? el.height : 0}
        filters={filters}
        brightness={el.brightness ?? 0}
        contrast={el.contrast ?? 0}
        saturation={el.saturation ?? 0}
        blurRadius={el.blur ?? 0}
        draggable={!el.locked}
        onClick={onSelect}
        onTap={onSelect}
        onDragEnd={(e: Konva.KonvaEventObject<DragEvent>) => onChange({ x: e.target.x(), y: e.target.y() })}
        onTransformEnd={(e: Konva.KonvaEventObject<Event>) => {
          const node = e.target as Konva.Image;
          onChange({
            x: node.x(),
            y: node.y(),
            width: Math.max(10, node.width() * Math.abs(node.scaleX())),
            height: Math.max(10, node.height() * Math.abs(node.scaleY())),
            rotation: node.rotation(),
          });
          node.scaleX(scaleX);
          node.scaleY(scaleY);
        }}
      />
      {isSelected && (
        <Transformer
          ref={trRef}
          boundBoxFunc={(oldBox: Konva.Box, newBox: Konva.Box) => ({
            ...newBox,
            width: Math.max(10, newBox.width),
            height: Math.max(10, newBox.height),
          })}
        />
      )}
    </>
  );
};

const KonvaTextEl: React.FC<{
  el: TextElement;
  isSelected: boolean;
  onSelect: () => void;
  onChange: (patch: Partial<TextElement>) => void;
  stageRef: React.RefObject<Konva.Stage | null>;
}> = ({ el, isSelected, onSelect, onChange, stageRef }) => {
  const shapeRef = useRef<Konva.Text>(null);
  const trRef = useRef<Konva.Transformer>(null);

  useEffect(() => {
    if (isSelected && trRef.current && shapeRef.current) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected]);

  const handleDblClick = () => {
    const node = shapeRef.current;
    const stage = stageRef.current;
    if (!node || !stage) return;

    node.hide();
    trRef.current?.hide();

    const textPosition = node.getAbsolutePosition();
    const stageBox = stage.container().getBoundingClientRect();
    const areaPosition = { x: stageBox.left + textPosition.x, y: stageBox.top + textPosition.y };

    const textarea = document.createElement('textarea');
    document.body.appendChild(textarea);
    textarea.value = el.text;
    textarea.style.cssText = `
      position: fixed;
      top: ${areaPosition.y}px;
      left: ${areaPosition.x}px;
      width: ${node.width() * stage.scaleX()}px;
      min-height: ${node.height()}px;
      font-size: ${el.fontSize}px;
      font-family: ${el.fontFamily};
      font-weight: ${el.fontStyle.includes('bold') ? 'bold' : 'normal'};
      font-style: ${el.fontStyle.includes('italic') ? 'italic' : 'normal'};
      color: ${el.fill};
      background: transparent;
      border: 1px dashed #999;
      padding: 2px;
      outline: none;
      resize: none;
      line-height: ${el.lineHeight ?? 1.2};
      letter-spacing: ${el.letterSpacing ?? 0}px;
      text-transform: ${el.textTransform ?? 'none'};
      overflow: hidden;
      z-index: 9999;
      text-align: ${el.align};
      transform: rotate(${el.rotation}deg);
      transform-origin: top left;
    `;
    textarea.focus();

    const finishEditing = () => {
      onChange({ text: textarea.value });
      document.body.removeChild(textarea);
      node.show();
      trRef.current?.show();
      trRef.current?.getLayer()?.batchDraw();
    };

    textarea.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Escape') finishEditing();
    });
    textarea.addEventListener('blur', finishEditing);
  };

  const [bold, italic] = [el.fontStyle.includes('bold'), el.fontStyle.includes('italic')];

  const applyTransform = (text: string) => {
    if (el.textTransform === 'uppercase') return text.toUpperCase();
    if (el.textTransform === 'lowercase') return text.toLowerCase();
    // Title-case: first letter upper + rest lower per word, preserving all whitespace
    if (el.textTransform === 'capitalize')
      return text.replace(/\S+/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
    return text;
  };

  return (
    <>
      <Text
        ref={shapeRef}
        x={el.x}
        y={el.y}
        width={el.width}
        text={applyTransform(el.text)}
        fontSize={el.fontSize}
        fontFamily={el.fontFamily}
        fontStyle={`${bold ? 'bold' : ''} ${italic ? 'italic' : ''}`.trim() || 'normal'}
        fill={el.fill}
        align={el.align}
        rotation={el.rotation}
        opacity={el.opacity}
        visible={el.visible}
        letterSpacing={el.letterSpacing ?? 0}
        lineHeight={el.lineHeight ?? 1.2}
        stroke={el.strokeColor || undefined}
        strokeWidth={el.strokeColor ? (el.strokeWidth ?? 0) : 0}
        fillAfterStrokeEnabled={!!el.strokeColor && (el.strokePosition ?? 'outer') === 'outer'}
        shadowEnabled={el.shadowEnabled ?? false}
        shadowColor={el.shadowColor ?? '#000000'}
        shadowBlur={el.shadowBlur ?? 10}
        shadowOffsetX={el.shadowOffsetX ?? 0}
        shadowOffsetY={el.shadowOffsetY ?? 5}
        draggable
        onClick={onSelect}
        onTap={onSelect}
        onDblClick={handleDblClick}
        onDblTap={handleDblClick}
        onDragEnd={(e: Konva.KonvaEventObject<DragEvent>) => onChange({ x: e.target.x(), y: e.target.y() })}
        onTransformEnd={(e: Konva.KonvaEventObject<Event>) => {
          const node = e.target as Konva.Text;
          onChange({
            x: node.x(),
            y: node.y(),
            width: Math.max(20, node.width() * node.scaleX()),
            rotation: node.rotation(),
          });
          node.scaleX(1);
          node.scaleY(1);
        }}
      />
      {isSelected && (
        <Transformer
          ref={trRef}
          enabledAnchors={['middle-left', 'middle-right']}
          boundBoxFunc={(_: Konva.Box, newBox: Konva.Box) => ({ ...newBox, width: Math.max(20, newBox.width) })}
        />
      )}
    </>
  );
};

const KonvaRectEl: React.FC<{
  el: RectElement;
  isSelected: boolean;
  onSelect: () => void;
  onChange: (patch: Partial<RectElement>) => void;
}> = ({ el, isSelected, onSelect, onChange }) => {
  const shapeRef = useRef<Konva.Rect>(null);
  const trRef = useRef<Konva.Transformer>(null);

  useEffect(() => {
    if (isSelected && trRef.current && shapeRef.current) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected]);

  return (
    <>
      <Rect
        ref={shapeRef}
        x={el.x} y={el.y}
        width={el.width} height={el.height}
        fill={el.fill} stroke={el.stroke} strokeWidth={el.strokeWidth}
        cornerRadius={el.cornerRadius}
        rotation={el.rotation} opacity={el.opacity} visible={el.visible}
        draggable
        onClick={onSelect} onTap={onSelect}
        onDragEnd={(e: Konva.KonvaEventObject<DragEvent>) => onChange({ x: e.target.x(), y: e.target.y() })}
        onTransformEnd={(e: Konva.KonvaEventObject<Event>) => {
          const node = e.target as Konva.Rect;
          onChange({
            x: node.x(), y: node.y(),
            width: Math.max(5, node.width() * node.scaleX()),
            height: Math.max(5, node.height() * node.scaleY()),
            rotation: node.rotation(),
          });
          node.scaleX(1); node.scaleY(1);
        }}
      />
      {isSelected && (
        <Transformer
          ref={trRef}
          boundBoxFunc={(_: Konva.Box, newBox: Konva.Box) => ({
            ...newBox,
            width: Math.max(5, newBox.width),
            height: Math.max(5, newBox.height),
          })}
        />
      )}
    </>
  );
};

const KonvaCircleEl: React.FC<{
  el: CircleElement;
  isSelected: boolean;
  onSelect: () => void;
  onChange: (patch: Partial<CircleElement>) => void;
}> = ({ el, isSelected, onSelect, onChange }) => {
  const shapeRef = useRef<Konva.Ellipse>(null);
  const trRef = useRef<Konva.Transformer>(null);

  useEffect(() => {
    if (isSelected && trRef.current && shapeRef.current) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected]);

  return (
    <>
      <Ellipse
        ref={shapeRef}
        x={el.x + el.width / 2} y={el.y + el.height / 2}
        radiusX={el.width / 2} radiusY={el.height / 2}
        fill={el.fill} stroke={el.stroke} strokeWidth={el.strokeWidth}
        rotation={el.rotation} opacity={el.opacity} visible={el.visible}
        draggable
        onClick={onSelect} onTap={onSelect}
        onDragEnd={(e: Konva.KonvaEventObject<DragEvent>) =>
          onChange({ x: e.target.x() - el.width / 2, y: e.target.y() - el.height / 2 })
        }
        onTransformEnd={(e: Konva.KonvaEventObject<Event>) => {
          const node = e.target as Konva.Ellipse;
          const sx = Math.abs(node.scaleX());
          const sy = Math.abs(node.scaleY());
          const newW = Math.max(5, el.width * sx);
          const newH = Math.max(5, el.height * sy);
          onChange({
            x: node.x() - newW / 2,
            y: node.y() - newH / 2,
            width: newW,
            height: newH,
            rotation: node.rotation(),
          });
          node.scaleX(1); node.scaleY(1);
        }}
      />
      {isSelected && (
        <Transformer
          ref={trRef}
          boundBoxFunc={(_: Konva.Box, newBox: Konva.Box) => ({
            ...newBox,
            width: Math.max(5, newBox.width),
            height: Math.max(5, newBox.height),
          })}
        />
      )}
    </>
  );
};

const KonvaLineEl: React.FC<{
  el: LineElement;
  isSelected: boolean;
  onSelect: () => void;
  onChange: (patch: Partial<LineElement>) => void;
}> = ({ el, isSelected, onSelect, onChange }) => {
  const shapeRef = useRef<Konva.Line>(null);
  const trRef = useRef<Konva.Transformer>(null);

  useEffect(() => {
    if (isSelected && trRef.current && shapeRef.current) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected]);

  return (
    <>
      <KonvaLine
        ref={shapeRef}
        x={el.x} y={el.y}
        points={[0, 0, el.width, 0]}
        stroke={el.stroke}
        strokeWidth={el.strokeWidth}
        lineCap={el.cap}
        rotation={el.rotation}
        opacity={el.opacity}
        visible={el.visible}
        hitStrokeWidth={Math.max(el.strokeWidth, 14)}
        draggable
        onClick={onSelect} onTap={onSelect}
        onDragEnd={(e: Konva.KonvaEventObject<DragEvent>) =>
          onChange({ x: e.target.x(), y: e.target.y() })
        }
        onTransformEnd={(e: Konva.KonvaEventObject<Event>) => {
          const node = e.target as Konva.Line;
          onChange({
            x: node.x(),
            y: node.y(),
            width: Math.max(10, el.width * Math.abs(node.scaleX())),
            rotation: node.rotation(),
          });
          node.scaleX(1); node.scaleY(1);
        }}
      />
      {isSelected && (
        <Transformer
          ref={trRef}
          enabledAnchors={['middle-left', 'middle-right']}
          boundBoxFunc={(_: Konva.Box, newBox: Konva.Box) => ({ ...newBox, width: Math.max(10, newBox.width) })}
        />
      )}
    </>
  );
};

const KonvaTriangleEl: React.FC<{
  el: TriangleElement;
  isSelected: boolean;
  onSelect: () => void;
  onChange: (patch: Partial<TriangleElement>) => void;
}> = ({ el, isSelected, onSelect, onChange }) => {
  const shapeRef = useRef<Konva.Shape>(null);
  const trRef = useRef<Konva.Transformer>(null);

  useEffect(() => {
    if (isSelected && trRef.current && shapeRef.current) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected]);

  return (
    <>
      <Shape
        ref={shapeRef}
        x={el.x} y={el.y}
        width={el.width} height={el.height}
        fill={el.fill} stroke={el.stroke} strokeWidth={el.strokeWidth}
        rotation={el.rotation} opacity={el.opacity} visible={el.visible}
        sceneFunc={(ctx: Konva.Context, shape: Konva.Shape) => {
          ctx.beginPath();
          ctx.moveTo(shape.width() / 2, 0);
          ctx.lineTo(shape.width(), shape.height());
          ctx.lineTo(0, shape.height());
          ctx.closePath();
          ctx.fillStrokeShape(shape);
        }}
        draggable
        onClick={onSelect} onTap={onSelect}
        onDragEnd={(e: Konva.KonvaEventObject<DragEvent>) =>
          onChange({ x: e.target.x(), y: e.target.y() })
        }
        onTransformEnd={(e: Konva.KonvaEventObject<Event>) => {
          const node = e.target as Konva.Shape;
          onChange({
            x: node.x(), y: node.y(),
            width: Math.max(5, node.width() * node.scaleX()),
            height: Math.max(5, node.height() * node.scaleY()),
            rotation: node.rotation(),
          });
          node.scaleX(1); node.scaleY(1);
        }}
      />
      {isSelected && (
        <Transformer
          ref={trRef}
          boundBoxFunc={(_: Konva.Box, newBox: Konva.Box) => ({
            ...newBox,
            width: Math.max(5, newBox.width),
            height: Math.max(5, newBox.height),
          })}
        />
      )}
    </>
  );
};

interface DesignerCanvasProps {
  elements: CanvasElement[];
  selectedId: string | null;
  background: string;
  stageWidth: number;
  stageHeight: number;
  guides: CanvasGuides;
  stageRef: React.RefObject<Konva.Stage | null>;
  onSelectId: (id: string | null) => void;
  onUpdateElement: (id: string, patch: Partial<CanvasElement>) => void;
}

const DesignerCanvas: React.FC<DesignerCanvasProps> = ({
  elements, selectedId, background, stageWidth, stageHeight, guides,
  stageRef, onSelectId, onUpdateElement,
}) => {
  const handleStageClick = useCallback((e: Konva.KonvaEventObject<MouseEvent>) => {
    if (e.target === e.target.getStage()) onSelectId(null);
  }, [onSelectId]);

  const { bleedPx, backWidthPx, spineWidthPx, frontStartPx } = guides;
  const spine1X   = bleedPx + backWidthPx;
  const spine2X   = bleedPx + backWidthPx + spineWidthPx;
  const labelSize = Math.max(7, Math.round(bleedPx * 0.6));

  return (
    <div
      style={{ position: 'relative', boxShadow: '0 4px 24px rgba(0,0,0,0.18)', display: 'inline-block', lineHeight: 0 }}
      onContextMenu={e => e.preventDefault()}
    >
      <KonvaStage
        ref={stageRef as React.RefObject<Konva.Stage>}
        width={stageWidth}
        height={stageHeight}
        onClick={handleStageClick}
        onTap={handleStageClick}
        style={{ cursor: 'default' }}
      >
        <Layer>
          {/* Background */}
          <Rect x={0} y={0} width={stageWidth} height={stageHeight} fill={background} listening={false} />

          {/* Elements */}
          {elements.map(el => {
            const isSelected = el.id === selectedId && !el.locked;
            const onSelect = () => { if (!el.locked) onSelectId(el.id); };
            if (el.type === 'image') {
              return (
                <KonvaImageEl key={el.id} el={el as ImageElement} isSelected={isSelected}
                  onSelect={onSelect} onChange={patch => onUpdateElement(el.id, patch)} />
              );
            }
            if (el.type === 'text') {
              return (
                <KonvaTextEl key={el.id} el={el as TextElement} isSelected={isSelected}
                  onSelect={onSelect} onChange={patch => onUpdateElement(el.id, patch)}
                  stageRef={stageRef} />
              );
            }
            if (el.type === 'rect') {
              return (
                <KonvaRectEl key={el.id} el={el as RectElement} isSelected={isSelected}
                  onSelect={onSelect} onChange={patch => onUpdateElement(el.id, patch)} />
              );
            }
            if (el.type === 'circle') {
              return (
                <KonvaCircleEl key={el.id} el={el as CircleElement} isSelected={isSelected}
                  onSelect={onSelect} onChange={patch => onUpdateElement(el.id, patch)} />
              );
            }
            if (el.type === 'line') {
              return (
                <KonvaLineEl key={el.id} el={el as LineElement} isSelected={isSelected}
                  onSelect={onSelect} onChange={patch => onUpdateElement(el.id, patch)} />
              );
            }
            if (el.type === 'triangle') {
              return (
                <KonvaTriangleEl key={el.id} el={el as TriangleElement} isSelected={isSelected}
                  onSelect={onSelect} onChange={patch => onUpdateElement(el.id, patch)} />
              );
            }
            return null;
          })}
        </Layer>
      </KonvaStage>

      {/* ── Guide overlay — DOM div, never captured by toDataURL() ── */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        {/* Bleed bands */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: bleedPx, background: 'rgba(0,0,0,0.18)' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: bleedPx, background: 'rgba(0,0,0,0.18)' }} />
        <div style={{ position: 'absolute', top: bleedPx, bottom: bleedPx, left: 0, width: bleedPx, background: 'rgba(0,0,0,0.18)' }} />
        <div style={{ position: 'absolute', top: bleedPx, bottom: bleedPx, right: 0, width: bleedPx, background: 'rgba(0,0,0,0.18)' }} />

        {/* Dashed bleed boundary */}
        <div style={{ position: 'absolute', top: bleedPx, left: bleedPx, right: bleedPx, bottom: bleedPx, border: '1px dashed rgba(200,0,0,0.5)' }} />

        {/* Back / spine divider */}
        <div style={{ position: 'absolute', top: bleedPx, bottom: bleedPx, left: spine1X, width: 1, borderLeft: '1px dashed rgba(200,0,0,0.6)' }} />
        {/* Spine / front divider */}
        <div style={{ position: 'absolute', top: bleedPx, bottom: bleedPx, left: spine2X, width: 1, borderLeft: '1px dashed rgba(200,0,0,0.6)' }} />

        {/* Panel labels */}
        <div style={{ position: 'absolute', top: bleedPx + 4, left: bleedPx, width: backWidthPx, textAlign: 'center', fontSize: labelSize, fontFamily: 'Arial', color: 'rgba(180,0,0,0.65)', userSelect: 'none' }}>BACK</div>
        {spineWidthPx > 20 && (
          <div style={{ position: 'absolute', top: bleedPx + 4, left: spine1X, width: spineWidthPx, textAlign: 'center', fontSize: Math.min(labelSize, Math.floor(spineWidthPx / 4)), fontFamily: 'Arial', color: 'rgba(180,0,0,0.65)', userSelect: 'none' }}>SPINE</div>
        )}
        <div style={{ position: 'absolute', top: bleedPx + 4, left: frontStartPx, width: backWidthPx, textAlign: 'center', fontSize: labelSize, fontFamily: 'Arial', color: 'rgba(180,0,0,0.65)', userSelect: 'none' }}>FRONT</div>

        {/* Bleed corner label */}
        <div style={{ position: 'absolute', top: 2, left: 3, fontSize: Math.max(6, labelSize - 2), fontFamily: 'Arial', color: 'rgba(255,255,255,0.8)', userSelect: 'none' }}>BLEED</div>
      </div>
    </div>
  );
};

export default DesignerCanvas;
